import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import SectionInnerPageBanner from '../../sections/common/banner';
import { TypeSectionBanner } from '../../@types/sectionTypes';
import { route, BANNER_TYPE } from '../../../globals/constants';
import { navUrl } from '../../../globals/common-functions';
import AvertImage from '../../../globals/elements/avertimg';
import { projectsData } from '../../../data/projects';

const ProjectDetailPage: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const project = projectId ? projectsData[projectId] : null;

    if (!project) {
        return (
            <div className="av-pj-v2">
                <SectionInnerPageBanner _type={BANNER_TYPE.COMPACT} _data={{
                    title: "Project Not Found",
                    crumb: "Success Stories",
                    image: ""
                }} />
                <div className="av-section-v2">
                    <div className="container text-center">
                        <h2>Project Not Found</h2>
                        <p>The project you're looking for doesn't exist or has been moved.</p>
                        <NavLink to={navUrl.pages(route.pages.CASE_STUDY)} className="av-btn-white" style={{background: '#007bff', color: '#fff', padding: '14px 32px', borderRadius: '10px', display: 'inline-block', textDecoration: 'none', fontWeight: 600}}>
                            View All Success Stories
                        </NavLink>
                    </div>
                </div>
            </div>
        );
    }

    const bannerData: TypeSectionBanner = {
        title: project.title,
        crumb: "Success Stories",
        image: ""
    };

    const allImages = project.images || [project.image];
    const isMobileProject = project.projectType === 'mobile';

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    };

    return (
        <div className="av-pj-v2">
            <SectionInnerPageBanner _type={BANNER_TYPE.COMPACT} _data={bannerData} />

            {/* Intro */}
            <div className="av-section-v2" style={{paddingBottom: 40}}>
                <div className="container">
                    <div className="row align-items-start">
                        <div className="col-lg-8 m-b30">
                            <div className="av-pj-cats">
                                {project.categories.map((category, index) => (
                                    <span key={index} className="av-pj-cat">{category}</span>
                                ))}
                                <span className="av-pj-type">
                                    <i className={`feather feather-${isMobileProject ? 'smartphone' : 'globe'}`}></i>
                                    {isMobileProject ? 'Mobile App' : 'Web Platform'}
                                </span>
                            </div>
                            <h1 className="av-pj-title">{project.title}</h1>
                            <p className="av-pj-desc">{project.description}</p>
                        </div>
                        <div className="col-lg-4 m-b30">
                            <div className="av-pj-tech-panel">
                                <h4>Technologies</h4>
                                <div className="av-pj-tech-list">
                                    {project.technologies.map((tech, index) => (
                                        <span key={index} className="av-pill">{tech}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Carousel */}
            <div style={{paddingBottom: 80}}>
                <div className="container">
                    <div className={`av-pj-gallery ${isMobileProject ? 'av-pj-gallery-mobile' : ''}`}>
                        <div className="av-pj-gallery-img">
                            <AvertImage
                                src={allImages[currentImageIndex]}
                                alt={`${project.title} - Screenshot ${currentImageIndex + 1}`}
                            />
                        </div>
                        {allImages.length > 1 && (
                            <>
                                <div
                                    className="av-pj-nav av-pj-nav-prev"
                                    role="button"
                                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                                    style={{cursor: 'pointer'}}
                                >
                                    &#8249;
                                </div>
                                <div
                                    className="av-pj-nav av-pj-nav-next"
                                    role="button"
                                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                                    style={{cursor: 'pointer'}}
                                >
                                    &#8250;
                                </div>
                                <div className="av-pj-dots">
                                    {allImages.map((_, index) => (
                                        <div
                                            key={index}
                                            className={`av-pj-dot ${index === currentImageIndex ? 'active' : ''}`}
                                            role="button"
                                            onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                                            style={{cursor: 'pointer'}}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Challenge & Solution */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 m-b30">
                            <div className="av-pj-content-card">
                                <div className="av-pj-content-icon">
                                    <i className="feather feather-help-circle"></i>
                                </div>
                                <h3>The Challenge</h3>
                                <p>{project.challenge}</p>
                            </div>
                        </div>
                        <div className="col-lg-6 m-b30">
                            <div className="av-pj-content-card av-pj-content-card-dark">
                                <div className="av-pj-content-icon">
                                    <i className="feather feather-check-circle"></i>
                                </div>
                                <h3>Our Solution</h3>
                                <p>{project.solution}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features */}
            <div className="av-section-v2" style={{paddingTop: 60}}>
                <div className="container">
                    <div className="av-sh">
                        <div className="av-sh-label">What We Delivered</div>
                        <h2>Key Features</h2>
                    </div>
                    <div className="row">
                        {project.features.map((feature, index) => (
                            <div key={index} className="col-lg-6 m-b20">
                                <div className="av-pj-feature">
                                    <i className="feather feather-check"></i>
                                    <span>{feature}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-cta-v2">
                        <h3>Interested in something similar?</h3>
                        <p>We'd love to hear about your project. Let's talk about what you need and how we can help.</p>
                        <div className="av-cta-actions">
                            <NavLink to="/contact-us" className="av-btn-white">Start Your Project</NavLink>
                            <NavLink to={navUrl.pages(route.pages.CASE_STUDY)} className="av-btn-ghost">
                                <i className="feather feather-arrow-left"></i> Back to Success Stories
                            </NavLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetailPage;
